import React, { useState } from 'react';
import { Alert, Box, Button, Card, Checkbox, Chip, Stack, Typography } from '@mui/joy';
import { Link as RouterLink } from 'react-router-dom';
import { VD_SCENARIOS, type VdScenarioFixture, type VdScenarioId, type VdScenarioStep } from './vdScenario.types';
import { purgeHarnessFixture, runVdScenario } from './vdScenarioEngine';

export default function VdScenarioEnginePanel() {
  const [busyId, setBusyId] = useState<VdScenarioId | null>(null);
  const [purgeAfter, setPurgeAfter] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<{
    scenarioId: VdScenarioId;
    fixture: VdScenarioFixture;
    steps: VdScenarioStep[];
    purged: boolean;
  } | null>(null);

  const run = async (scenarioId: VdScenarioId) => {
    setBusyId(scenarioId);
    setError(null);
    try {
      const result = await runVdScenario(scenarioId, { purgeAfter });
      setLast({
        scenarioId,
        fixture: result.fixture,
        steps: result.steps,
        purged: purgeAfter,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const purgeLast = async () => {
    if (!last || last.purged) return;
    setBusyId(last.scenarioId);
    setError(null);
    try {
      await purgeHarnessFixture(last.fixture);
      setLast({ ...last, purged: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack gap={2}>
      <Alert color="warning" variant="soft">
        This is the live environment. The engine never opens an existing workspace. Each run creates a new
        customer, workspace, and Visual Designer quote tagged VDTEST, then mocks the customer accept or reject
        without sending email.
      </Alert>
      <Checkbox
        checked={purgeAfter}
        onChange={(event) => setPurgeAfter(event.target.checked)}
        label="Purge the throwaway workspace when the run finishes"
      />
      <Stack gap={1.25}>
        {VD_SCENARIOS.map((scenario) => (
          <Card key={scenario.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Typography level="title-sm">{scenario.title}</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.600' }}>{scenario.summary}</Typography>
              </Box>
              <Button
                size="sm"
                loading={busyId === scenario.id}
                disabled={busyId != null}
                onClick={() => { void run(scenario.id); }}
              >
                Run
              </Button>
            </Stack>
          </Card>
        ))}
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
      {last && (
        <Card variant="soft" sx={{ p: 1.5 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Last run</Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            {last.fixture.lifecycleRef} · {last.purged ? 'purged' : 'kept for inspection'}
          </Typography>
          <Stack gap={0.5} sx={{ mb: 1.25 }}>
            {last.steps.map((step) => (
              <Stack key={step.id} direction="row" gap={1} alignItems="center">
                <Chip size="sm" color={step.status === 'pass' ? 'success' : 'danger'}>{step.status}</Chip>
                <Typography level="body-sm">{step.label}</Typography>
                {step.detail && (
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{step.detail}</Typography>
                )}
              </Stack>
            ))}
          </Stack>
          {!last.purged && (
            <Stack direction="row" gap={1}>
              <Button
                size="sm"
                variant="outlined"
                component={RouterLink}
                to={`/projects/${last.fixture.projectId}/workspace`}
              >
                Open workspace
              </Button>
              <Button size="sm" color="danger" variant="outlined" loading={busyId != null} onClick={() => { void purgeLast(); }}>
                Purge this run
              </Button>
            </Stack>
          )}
        </Card>
      )}
    </Stack>
  );
}
